import { useParams, Navigate } from "react-router-dom"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useAuthContext } from "@/contexts/AuthContext"
import {
  useTournamentDetail,
  useTournamentOrganizers,
  useTournamentPlayers,
} from "@/graphql/hooks/useTournamentDetail"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertCircle } from "lucide-react"
import { HeaderTournament, TournamentNavigation } from "@/components/TournamentNavigation"
import { TournamentRaceStats } from "@/components/TournamentRaceStats"

const TournamentReport = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>()
  const { userInfo, loading: authLoading, isAuthenticated, isAdmin } = useAuthContext()

  const {
    tournament,
    loading: tournamentLoading,
    error,
  } = useTournamentDetail(Number(tournamentId))

  const { organizers, loading: loadingOrganizers } = useTournamentOrganizers(
    Number(tournamentId),
    isAuthenticated,
  )

  const { players } = useTournamentPlayers(Number(tournamentId))

  // Check authentication
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (authLoading || tournamentLoading || loadingOrganizers) {
    return (
      <Layout>
        <LoadingScreen message="Cargando reporte..." />
      </Layout>
    )
  }
  // Check authorization
  const canAccess = () => {
    if (authLoading || !userInfo || !tournament) return false

    // Check if user owns the store that owns this tournament
    const isStoreOwner = userInfo.User?.Person?.Store?.id === tournament.storeId

    const isOrganizer = organizers?.some(({ personId }) => personId === userInfo?.User?.Person?.id)

    return isAdmin || isStoreOwner || isOrganizer
  }

  if (error || !tournament) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-muted-foreground mb-4">
                Torneo no encontrado
              </h2>
              <p className="text-muted-foreground">
                No se pudo cargar la información del torneo solicitado.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  if (!canAccess()) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No tienes permisos para acceder al reporte de este torneo.
            </AlertDescription>
          </Alert>
          <TournamentNavigation
            tournament={tournament}
            tournamentName={tournament.name || ""}
            currentPage="report"
            tournamentStatusId={tournament.statusId}
          />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <HeaderTournament tournament={tournament} />
      <div className="container mx-auto px-0 py-0">
        <TournamentNavigation
          tournament={tournament}
          tournamentName={tournament.name || ""}
          currentPage="report"
          tournamentStatusId={tournament.statusId}
          showRounds={false}
        />

        {userInfo.User.Person.id !== 1 ? (
          <Card>
            <CardHeader>
              <CardTitle>Estadísticas del Torneo</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center text-muted-foreground py-8">
                Reporte de razas y participación en desarrollo...
              </p>
            </CardContent>
          </Card>
        ) : (
          <TournamentRaceStats tournament={tournament} players={players} />
        )}
      </div>
    </Layout>
  )
}

export default TournamentReport
